import { useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import Reveal from "../components/Reveal";

const BEFORE_PROBLEMS = ["Unclear messaging", "Weak hierarchy", "Generic design", "Confusing CTA", "Poor on mobile"];
const AFTER_WINS = ["Clear story", "Premium hierarchy", "Obvious CTA", "Mobile responsive", "Built on credibility"];

const BeforeMock = () => (
  <div className="flex h-full flex-col bg-[#E8E8E6] p-5 text-left saturate-0 sm:p-7">
    <div className="flex items-center justify-between border-b border-gray-400 pb-3">
      <div className="h-3 w-20 rounded-sm bg-gray-500" />
      <div className="flex gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-2 w-8 rounded-sm bg-gray-400" />
        ))}
      </div>
    </div>
    <p className="mt-6 font-serif text-xs italic text-gray-500">Welcome To Our Website!!</p>
    <div className="mt-2 h-4 w-3/4 rounded-sm bg-gray-500" />
    <div className="mt-1.5 h-4 w-1/2 rounded-sm bg-gray-400" />
    <div className="mt-5 space-y-2">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-2 w-full rounded-sm bg-gray-300" />
      ))}
      <div className="h-2 w-2/3 rounded-sm bg-gray-300" />
    </div>
    <div className="mt-5 flex items-center gap-3">
      <div className="rounded-sm bg-blue-700 px-3 py-1.5 text-[9px] font-bold uppercase text-white">Click Here</div>
      <div className="h-2 w-16 rounded-sm bg-gray-400" />
    </div>
    <div className="mt-auto grid grid-cols-3 gap-2 pt-5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-12 rounded-sm border border-gray-400 bg-gray-300" />
      ))}
    </div>
  </div>
);

const AfterMock = () => (
  <div className="flex h-full flex-col bg-navy p-5 text-left sm:p-7">
    <div className="flex items-center justify-between border-b border-navyline pb-3">
      <div className="flex items-center gap-2">
        <div className="h-3 w-3 rotate-45 rounded-[2px] bg-gold" />
        <div className="h-2.5 w-16 rounded-sm bg-white/80" />
      </div>
      <div className="rounded-full bg-gold px-3 py-1 text-[8px] font-bold uppercase tracking-wider text-ink">Book Now</div>
    </div>
    <div className="mt-6 h-1.5 w-16 rounded-full bg-gold/70" />
    <div className="mt-3 h-5 w-4/5 rounded-sm bg-white" />
    <div className="mt-1.5 h-5 w-3/5 rounded-sm bg-gold" />
    <div className="mt-4 space-y-2">
      <div className="h-2 w-full rounded-sm bg-white/25" />
      <div className="h-2 w-5/6 rounded-sm bg-white/25" />
    </div>
    <div className="mt-5 flex items-center gap-3">
      <div className="rounded-full bg-gold px-4 py-2 text-[9px] font-bold uppercase tracking-wider text-ink">
        Start My Project
      </div>
      <div className="rounded-full border border-white/30 px-4 py-2 text-[9px] font-semibold uppercase tracking-wider text-white/70">
        See Our Work
      </div>
    </div>
    <div className="mt-auto grid grid-cols-3 gap-2 pt-5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex h-12 flex-col justify-end gap-1.5 rounded-md border border-navyline bg-navycard p-2">
          <div className="h-1.5 w-1.5 rotate-45 bg-gold" />
          <div className="h-1.5 w-4/5 rounded-sm bg-white/30" />
        </div>
      ))}
    </div>
  </div>
);

export default function BeforeAfter() {
  const [pos, setPos] = useState(50);
  const trackRef = useRef(null);

  const updateFromClientX = (clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };

  return (
    <section id="transformation" data-testid="transformation-section" className="relative bg-navy py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="font-mono text-xs font-semibold tracking-[0.3em] text-gold">THE DIFFERENCE</p>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            See What Better Feels Like.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-400">
            Drag the handle. The gap between a generic website and a story-driven digital experience is the value we
            build.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative mx-auto mt-14 max-w-5xl">
            <div
              ref={trackRef}
              data-testid="before-after-slider"
              role="slider"
              aria-label="Before and after comparison"
              aria-valuenow={Math.round(pos)}
              aria-valuemin={0}
              aria-valuemax={100}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 5));
                if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 5));
              }}
              onPointerDown={onPointerDown}
              onPointerMove={(e) => e.buttons === 1 && updateFromClientX(e.clientX)}
              className="relative h-[380px] cursor-ew-resize touch-none select-none overflow-hidden rounded-3xl border border-navyline shadow-2xl shadow-black/50 sm:h-[440px]"
            >
              <div className="absolute inset-0">
                <BeforeMock />
              </div>
              <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
                <AfterMock />
              </div>
              <div
                className="absolute inset-y-0 z-10 w-px bg-gold"
                style={{ left: `${pos}%` }}
                aria-hidden="true"
              >
                <span className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gold/60 bg-ink shadow-lg shadow-black/50">
                  <MoveHorizontal className="h-4 w-4 text-gold" />
                </span>
              </div>
              <span className="absolute left-4 top-4 rounded-full bg-black/50 px-3 py-1 font-mono text-[10px] font-semibold tracking-[0.2em] text-gray-300 backdrop-blur">
                BEFORE
              </span>
              <span className="absolute right-4 top-4 rounded-full bg-gold px-3 py-1 font-mono text-[10px] font-bold tracking-[0.2em] text-ink">
                AFTER — AT DIGITAL
              </span>
            </div>
            <p className="mt-3 text-center font-mono text-[10px] tracking-[0.2em] text-slate-600">
              ILLUSTRATIVE EXAMPLE — NOT A REAL CLIENT SITE
            </p>
            <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
              <ul className="space-y-2.5">
                {BEFORE_PROBLEMS.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm text-slate-500">
                    <span className="h-1 w-1 rounded-full bg-slate-600" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
              <ul className="space-y-2.5">
                {AFTER_WINS.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm text-slate-200">
                    <span className="h-1.5 w-1.5 rotate-45 bg-gold" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
